import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { IconBrandTelegram } from '@tabler/icons-react';

import Checked from 'src/icons/Checked';

import type { LeadExampleChoiceValue } from 'src/utils/leadStreamFilterProfile';
import type { ILeadExample } from 'src/services/NicheAnalysisService';

export const hidePreviewContacts = (value: string) =>
    value
        .replace(/https?:\/\/\S+|(?:www\.|t\.me\/|telegram\.me\/|wa\.me\/)\S+/gi, '[ссылка скрыта]')
        .replace(/[\w.+-]+@[\w.-]+\.[a-z]{2,}/gi, '[почта скрыта]')
        .replace(/@[a-z0-9_]{4,}/gi, '[контакт скрыт]')
        .replace(/(?:\+?7|8)[\s()-]*(?:\d[\s()-]*){10}|\b\d{10,11}\b/g, '[телефон скрыт]')
        .replace(/\s+/g, ' ')
        .trim();

interface Props {
    examples: ILeadExample[];
    choices: Record<number, LeadExampleChoiceValue>;
    onChoose: (exampleId: number, choice: LeadExampleChoiceValue | null) => void;
    onLoadMore?: () => Promise<void>;
    collapsibleAfterReview?: boolean;
    trainingControls?: ReactNode;
    choicesDisabled?: boolean;
}

const previewText = (example: ILeadExample) => hidePreviewContacts(example.text || '').slice(0, 220);

export const mergeDemandExamples = (existing: ILeadExample[], incoming: ILeadExample[], category?: string) => {
    const result: ILeadExample[] = [];
    const ids = new Set<number>();
    const texts = new Set<string>();
    const categoryKey = category?.trim().toLowerCase();
    const append = (example: ILeadExample) => {
        const textKey = previewText(example).toLowerCase();
        if (!textKey || ids.has(example.id) || texts.has(textKey)) return;
        ids.add(example.id);
        texts.add(textKey);
        result.push(example);
    };
    existing.forEach(append);
    incoming.forEach((example) => {
        if (categoryKey && example.category?.trim().toLowerCase() !== categoryKey) return;
        append(example);
    });
    return result;
};

const feedbackButtonSx = (isActive: boolean, tone: 'success' | 'danger') => ({
    width: { xs: 40, sm: 34 },
    height: { xs: 40, sm: 34 },
    border: `1px solid ${isActive ? `var(--lm-${tone}-border)` : 'var(--lm-border)'}`,
    borderRadius: '8px',
    color: isActive ? `var(--lm-${tone}-ink)` : 'var(--lm-muted)',
    bgcolor: isActive ? `var(--lm-${tone}-bg)` : 'var(--lm-surface)',
    p: 0,
    '&:hover': { borderColor: `var(--lm-${tone}-border)`, bgcolor: `var(--lm-${tone}-bg)` },
});

export default function LeadDemandPreview({ examples, choices, onChoose, onLoadMore, collapsibleAfterReview = false, trainingControls, choicesDisabled = false }: Props) {
    const [visibleCount, setVisibleCount] = useState(3);
    const [reviewExpanded, setReviewExpanded] = useState(true);
    const [loadedMore, setLoadedMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const inFlight = useRef(false);
    const prepared = mergeDemandExamples([], examples).map((example) => ({
        ...example,
        preview: previewText(example),
    }));
    const visible = prepared.slice(0, visibleCount);
    const reviewComplete = collapsibleAfterReview && visible.length > 0 && visible.every((example) => Boolean(choices[example.id]));
    const collapsed = reviewComplete && !reviewExpanded;
    const hasLocalMore = prepared.length > visibleCount;
    const hasRemoteMore = Boolean(onLoadMore && !loadedMore);
    const loadMore = async () => {
        if (inFlight.current) return;
        if (hasLocalMore) {
            setVisibleCount(visible.length + 3);
            return;
        }
        if (!onLoadMore || loadedMore) return;
        inFlight.current = true;
        setLoading(true);
        setError('');
        try {
            await onLoadMore();
            setLoadedMore(true);
            setVisibleCount(visible.length + 3);
        } catch {
            setError('Не удалось загрузить примеры. Попробуйте ещё раз.');
        } finally {
            inFlight.current = false;
            setLoading(false);
        }
    };

    if (!visible.length) return collapsibleAfterReview ? (
        <Box component="section" aria-label="Примеры запросов" sx={{ borderTop: '1px solid var(--lm-border)', pt: 2 }}>
            <Typography component="h2" sx={{ color: 'var(--lm-text)', fontSize: { xs: 24, sm: 28 }, fontWeight: 850, lineHeight: 1.2, mb: 1 }}>
                Обучение ИИ на реальных примерах
            </Typography>
            <Typography sx={{ color: 'var(--lm-muted)', fontSize: 14 }}>Примеры для оценки пока не найдены.</Typography>
            {error && <Alert severity="info" sx={{ mt: 1.5 }}>{error}</Alert>}
            {onLoadMore && <Button type="button" variant="outlined" disabled={loading || loadedMore} onClick={() => void loadMore()} sx={{ mt: 1.5, textTransform: 'none' }}>
                {loading ? 'Загружаем…' : loadedMore ? 'Примеров пока нет' : error ? 'Повторить загрузку' : 'Загрузить примеры'}
            </Button>}
        </Box>
    ) : null;

    return (
        <Box
            component="section"
            aria-label="Примеры запросов"
            sx={{ borderTop: '1px solid var(--lm-border)', pt: 2 }}
        >
            <Typography
                component="h2"
                sx={{ color: 'var(--lm-text)', fontSize: { xs: 24, sm: 28 }, fontWeight: 850, lineHeight: 1.2, mb: 1 }}
            >
                Обучение ИИ на реальных примерах
            </Typography>
            {collapsed ? (
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
                    <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13 }}>Оценено примеров: {visible.length}</Typography>
                    <Button type="button" size="small" onClick={() => setReviewExpanded(true)} sx={{ textTransform: 'none' }}>Изменить оценки</Button>
                </Stack>
            ) : (<>
            <Typography sx={{ color: 'var(--lm-muted)', fontSize: 13, mb: 1.5 }}>
                Отметьте, какие запросы подходят, а какие нет. ИИ учтёт оценки в правилах вашего потока.
            </Typography>
            {trainingControls}
            <Stack spacing={1.25}>
                {visible.map((example, index) => (
                    <Box
                        key={`${example.id}-${index}`}
                        component="article"
                        sx={{
                            border: '1px solid var(--lm-border)',
                            borderRadius: '8px',
                            bgcolor: 'var(--lm-surface)',
                            p: 1.5,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1,
                                mb: 1,
                            }}
                        >
                            <Typography sx={{ color: 'var(--lm-text)', fontSize: 13, fontWeight: 700 }}>
                                {example.category_icon || '💬'} {example.category || 'Запрос клиента'}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    color: 'var(--lm-muted)',
                                    fontSize: 12,
                                    flexShrink: 0,
                                }}
                            >
                                <IconBrandTelegram
                                    size={15}
                                    stroke={1.8}
                                    aria-hidden="true"
                                />{' '}
                                Telegram
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                border: '1px solid var(--lm-border)',
                                borderRadius: '8px',
                                px: 1.25,
                                py: 1,
                                boxShadow: '0 2px 10px rgba(68, 73, 103, 0.06)',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'var(--lm-text)',
                                    fontSize: 14,
                                    lineHeight: 1.55,
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'anywhere',
                                }}
                            >
                                {example.preview}
                                {example.text.length > 220 ? '…' : ''}
                            </Typography>
                        </Box>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-end"
                            spacing={0.5}
                            sx={{ mt: 1 }}
                        >
                            {['fit', 'skip'].includes(choices[example.id]) && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        mr: 0.5,
                                        fontWeight: 600,
                                        color:
                                            choices[example.id] === 'fit'
                                                ? 'var(--lm-success-ink)'
                                                : 'var(--lm-danger-ink)',
                                    }}
                                >
                                    {choices[example.id] === 'fit' ? 'Подходит' : 'Не подходит'}
                                </Typography>
                            )}
                            <Tooltip title="Подходит">
                                <IconButton
                                    type="button"
                                    aria-label="Подходит"
                                    disabled={choicesDisabled}
                                    aria-pressed={choices[example.id] === 'fit'}
                                    sx={feedbackButtonSx(choices[example.id] === 'fit', 'success')}
                                    onClick={() => onChoose(example.id, choices[example.id] === 'fit' ? null : 'fit')}
                                >
                                    <Checked
                                        fillColor={
                                            choices[example.id] === 'fit' ? 'var(--lm-success-ink)' : 'currentColor'
                                        }
                                        fontColor="var(--lm-surface)"
                                    />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Не подходит">
                                <IconButton
                                    type="button"
                                    aria-label="Не подходит"
                                    disabled={choicesDisabled}
                                    aria-pressed={choices[example.id] === 'skip'}
                                    sx={feedbackButtonSx(choices[example.id] === 'skip', 'danger')}
                                    onClick={() => onChoose(example.id, choices[example.id] === 'skip' ? null : 'skip')}
                                >
                                    <Box
                                        component="span"
                                        aria-hidden="true"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 20,
                                            fontWeight: 400,
                                            lineHeight: '14px',
                                            width: 18,
                                        }}
                                    >
                                        ×
                                    </Box>
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Box>
                ))}
            </Stack>
            {error && (
                <Alert
                    severity="info"
                    sx={{ mt: 1.5 }}
                >
                    {error}
                </Alert>
            )}
            {hasLocalMore || hasRemoteMore ? (
                <Button
                    type="button"
                    variant="outlined"
                    disabled={loading}
                    onClick={() => void loadMore()}
                    startIcon={
                        loading ? (
                            <CircularProgress
                                size={16}
                                color="inherit"
                            />
                        ) : undefined
                    }
                    sx={{
                        mt: 1.5,
                        minHeight: 40,
                        borderRadius: '10px',
                        color: '#0044FF',
                        borderColor: 'var(--lm-border)',
                        textTransform: 'none',
                        width: { xs: '100%', sm: 'auto' },
                    }}
                >
                    {loading ? 'Загружаем…' : error ? 'Повторить загрузку' : 'Показать ещё'}
                </Button>
            ) : (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1.5 }}
                >
                    Больше примеров пока нет.
                </Typography>
            )}
            {reviewComplete && <Button type="button" size="small" onClick={() => setReviewExpanded(false)} sx={{ mt: 1, textTransform: 'none' }}>Свернуть примеры</Button>}
            </>)}
        </Box>
    );
}
